import {
    ReturnConsultationBillingPolicy,
    getReturnConsultationBillingPolicy,
} from '@/lib/api/financial/financial-settings-api'
import { useEffect, useState } from 'react'

/**
 * Cache de sessão da política.
 *
 * Mesma razão do `use-offline-settlement-policy`: é configuração administrativa,
 * muda raramente, e o formulário de agendamento é montado dezenas de vezes por
 * turno. Um refresh da página já a renova.
 */
let cached: ReturnConsultationBillingPolicy | null = null
let inFlight: Promise<ReturnConsultationBillingPolicy | null> | null = null

function fetchPolicy(): Promise<ReturnConsultationBillingPolicy | null> {
    if (cached) return Promise.resolve(cached)
    if (inFlight) return inFlight

    inFlight = getReturnConsultationBillingPolicy()
        .then(res => {
            // Sem permissão ou erro: assume o PADRÃO (não cobrar o retorno), que
            // é o mesmo valor devolvido pelo backend quando a empresa nunca
            // configurou nada. Errar para "cobrar" emitiria uma cobrança que a
            // recepção teria de cancelar à mão.
            if (!res.success || !res.result) return null
            cached = res.result
            return cached
        })
        .catch(() => null)
        .finally(() => {
            inFlight = null
        })

    return inFlight
}

/** Invalida o cache após salvar a política (tela de configurações). */
export function resetReturnConsultationBillingPolicyCache(): void {
    cached = null
}

/**
 * A consulta marcada como retorno deve gerar cobrança?
 *
 * Falso (o padrão) faz o agendamento de retorno nascer sem cobrança — o valor
 * fica guardado no atendimento e a emissão é manual, pelo "Gerar cobrança".
 */
export function useReturnConsultationBillingPolicy(): {
    chargeReturn: boolean
} {
    const [chargeReturn, setChargeReturn] = useState(
        cached?.chargeReturn ?? false
    )

    useEffect(() => {
        let cancelled = false
        fetchPolicy().then(policy => {
            if (!cancelled && policy) setChargeReturn(policy.chargeReturn)
        })
        return () => {
            cancelled = true
        }
    }, [])

    return { chargeReturn }
}
